import { useState, useEffect } from "react";

import ContentShowItem from "MiniSiteComponents/Carousel/Carousel.interface";
import TabPanel from "./Desktop_TabPanel";
import Tab from "./Desktop_Tab";

import Helper from "Interfaces/Helper";
import useA11y from "./useA11y";

const cls = {
  Tab: "react-tabs__tab",
  TabSelected: "react-tabs__tab--selected",
  TabDisabled: "react-tabs__tab--disabled",
  Tablist: "react-tabs__tab-list",
  Tabpanel: "react-tabs__tab-panel",
  TpanelSelected: "react-tabs__tab-panel--selected",
};

type Props = {
  componentTitle: string;
  componentId: string;
  items: ContentShowItem[];
  largeCircles: boolean;
  direction: string;
  isShow: boolean;
  closeAllButThis: string;
  tabItemId: string;
  activeTab: number;
  closeOthers: (tabItemId: string, selectedIdx: number) => void;
  setActiveIndexOnSelectedTab: (selectedIdx: number) => void;
  linkTarget?: string;
};

const TabsRow: React.FC<Props> = (props) => {
  const {
    componentId,
    componentTitle,
    largeCircles,
    isShow,
    closeAllButThis,
    direction,
    setActiveIndexOnSelectedTab,
    tabItemId,
    closeOthers,
    items,
    linkTarget,
  } = props;
  // STATE
  const [activeTab, setActiveTab] = useState(closeAllButThis == tabItemId ? props.activeTab : 999);
  const [isItemContentClamped, setIsItemContentClamped] = useState(false);
  const [isTabpanelContentExpended, setIsTabpanelContentExpended] = useState(false);

  const isrtl = direction == "rtl";
  const displayClass = isShow ? "" : " d-none ";
  const itemsNumClass = largeCircles ? "mst-four-tabs " : "";
  const linkIconType = isrtl ? "fa-chevron-left" : "fa-chevron-right";

  const { onBtnCloseKeyDown, onKeydown_tab } = useA11y({ componentId, tabItemId, activeTab, isrtl });

  const onMount = () => {
    const hasActiveTab = closeAllButThis != "" && closeAllButThis != tabItemId && activeTab < items.length;
    if (hasActiveTab) {
      closeActiveTab();
    }
    checkLongContent();
  };

  const handleTabSelect = (index: number, shouldRegisterGA = true) => {
    if (closeAllButThis != tabItemId) {
      closeOthers(tabItemId, index);
    } else {
      setActiveTab(index);
      setActiveIndexOnSelectedTab(index);
      setIsTabpanelContentExpended(false);
    }

    //GA3
    const itemTitle = items[index].title;
    if (shouldRegisterGA) {
      console.log("Registered!");
      Helper.handleLinkClicksGA4("Circles View", componentTitle, itemTitle);
    }
  };

  const closeActiveTab = () => {
    setActiveTab(999);
    setActiveIndexOnSelectedTab(999);
    setIsTabpanelContentExpended(false);
  };

  const getTabsList = () => document.querySelector("." + tabItemId);

  const checkLongContent = () => {
    if (activeTab >= items.length) return;

    const tabsList = getTabsList();
    const tabs = tabsList.querySelectorAll(`.${cls.Tab}`);
    const tabsPanel = tabsList.querySelector("[aria-labelledby='" + tabs[activeTab].id + "']");

    const panelContent = tabsPanel.querySelector(".mst-tabItem-contentBody");
    if (!panelContent) return;
    const allLinks = tabsPanel.querySelectorAll("a");
    Helper.GA4OuterClickEvent(allLinks);

    const isContentClamped = panelContent.clientHeight != panelContent.scrollHeight;

    if (isContentClamped !== isItemContentClamped && !isTabpanelContentExpended) {
      setIsItemContentClamped(isContentClamped);
    }
  };

  const toggleShowMore = () => {
    // GA on show click
    if (!isTabpanelContentExpended) {
      var activeItem = items[activeTab];
      Helper.handleLinkClicksGA4("Circles View", componentTitle, activeItem.title + "- more");
    }
    setIsTabpanelContentExpended((prev) => !prev);
  };

  const onTabFocus = (e: React.FocusEvent<HTMLLIElement | Element>, tabIdx: number) => {
    // Check if tab has contant / *not* only link
    const contentTab = e.currentTarget && !e.currentTarget.className.includes("js-only-link");
    if (contentTab) {
      // Set tab as selected
      handleTabSelect(tabIdx, false);
      // Focus elemant after re-render / DOM-mutations (because state change removes focus)
      setTimeout(() => {
        const el = document.getElementById(e.target.id);
        el.focus();
      }, 0);
    }
  };

  // On mount:
  useEffect(() => {
    onMount();
  }, []);

  // On 'activeTab' change:
  useEffect(() => {
    checkLongContent();
  }, [activeTab]);

  if (items.length == 0) return null;

  return (
    <div key={tabItemId} dir={direction} className={itemsNumClass + displayClass + tabItemId}>
      <ul className={cls.Tablist} role="tablist">
        {items.map((contentShowItem, idx) => {
          const linkUrl = contentShowItem.link.url;
          const isDisabled = !(
            contentShowItem.content ||
            linkUrl.startsWith("/") ||
            linkUrl.startsWith("http") ||
            linkUrl.startsWith("tel:") ||
            contentShowItem.url.startsWith("/") ||
            contentShowItem.url.startsWith("http")
          );
          const tablistRowIsActive = closeAllButThis === tabItemId;
          const isOnlyLink = !isDisabled && !contentShowItem.content;
          const isSelectedTab = idx === activeTab && tablistRowIsActive;
          const isNoActiveTab = activeTab === 999;
          const isFirstTabWhenNoActiveTabs = isNoActiveTab && idx === 0;
          const tabIndexValue = (isSelectedTab || isFirstTabWhenNoActiveTabs) && !isDisabled ? 0 : -1;
          const onClick = (e) => handleTabSelect(idx);

          return (
            <li
              onClick={isDisabled ? null : onClick}
              key={`${contentShowItem.index}_${idx}`}
              role="tab"
              tabIndex={tabIndexValue}
              onKeyDown={(e) => onKeydown_tab(e)}
              id={`Tab-${componentId}-${tabItemId}-${idx}`}
              aria-controls={`Tabpanel-${componentId}-${tabItemId}-${idx}`}
              aria-disabled={isDisabled}
              aria-selected={!isDisabled && isSelectedTab}
              className={`${cls.Tab} ${isSelectedTab ? cls.TabSelected : ""} ${isDisabled ? cls.TabDisabled : ""} ${isOnlyLink ? "only-link js-only-link" : ""}`}
            >
              <Tab contentShowItem={contentShowItem} linkIconType={linkIconType} linkTarget={linkTarget} />
            </li>
          );
        })}
      </ul>

      {items.map((contentShowItem, idx) => {
        const isSelectedTabpanel = idx === activeTab && closeAllButThis === tabItemId;

        return (
          <div
            role="tabpanel"
            key={`${contentShowItem.index}_${idx}`}
            aria-selected={isSelectedTabpanel}
            className={`${cls.Tabpanel} ${isSelectedTabpanel ? cls.TpanelSelected : "d-none"} ${!contentShowItem.content ? "d-none" : ""}`}
            id={`Tabpanel-${componentId}-${tabItemId}-${idx}`}
            aria-labelledby={`Tab-${componentId}-${tabItemId}-${idx}`}
          >
            {contentShowItem.content && (
              <TabPanel
                contentShowItem={contentShowItem}
                linkIconType={linkIconType}
                itemId={contentShowItem.index}
                onClosePanel={closeActiveTab}
                componentId={componentId}
                isItemContentClamped={isItemContentClamped}
                isTabpanelContentExpended={isTabpanelContentExpended}
                onBtnCloseKeyDown={onBtnCloseKeyDown}
                toggleShowMore={toggleShowMore}
                tabItemId={tabItemId}
                linkTarget={linkTarget}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TabsRow;
